import React, { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Lock, Maximize2, Monitor } from 'lucide-react';

type PreviewVariant = 'card' | 'hero' | 'panel';

interface LiveSitePreviewProps {
  name: string;
  liveUrl: string;
  variant?: PreviewVariant;
  className?: string;
}

/** Avoid embedding this portfolio site inside itself (recursive iframe). */
export function shouldEmbedLiveUrl(liveUrl: string | null | undefined): boolean {
  if (!liveUrl) return false;
  try {
    const url = new URL(liveUrl);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'e8solutions.ai' || host === 'e8solutions.io') {
      // Dedicated demo route is fine; root/app shell is not
      return url.pathname.startsWith('/the-green-light');
    }
    return true;
  } catch {
    return false;
  }
}

export function formatLiveHost(liveUrl: string): string {
  return liveUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const LiveSitePreview: React.FC<LiveSitePreviewProps> = ({
  name,
  liveUrl,
  variant = 'panel',
  className = '',
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const canEmbed = shouldEmbedLiveUrl(liveUrl);

  useEffect(() => {
    setIframeLoaded(false);
    setIframeError(false);
  }, [liveUrl]);

  // Some sites fire onLoad but still block framing — surface fallback after a short wait
  useEffect(() => {
    if (!canEmbed || iframeError) return;
    const timer = window.setTimeout(() => {
      if (!iframeLoaded) setIframeError(true);
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [canEmbed, iframeLoaded, iframeError, liveUrl]);

  const chrome = variant !== 'hero';
  const scaled = variant === 'card';
  const interactive = variant === 'panel' || variant === 'hero';

  const fallback = (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 px-6">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
          <Monitor className="w-7 h-7 text-white/40" />
        </div>
        <p className="text-white font-medium mb-1">{name}</p>
        <p className="text-white/50 text-sm mb-4">
          {canEmbed ? "This site can't be embedded here" : 'Open the live site to explore'}
        </p>
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0088cc] text-white rounded-lg text-sm font-medium hover:bg-[#006699] transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="w-4 h-4" />
          Open Full Site
        </a>
      </div>
    </div>
  );

  return (
    <div className={`relative overflow-hidden bg-gray-100 flex flex-col ${className}`}>
      {chrome && (
        <div
          className={`flex-shrink-0 z-10 flex items-center gap-1.5 bg-gray-200/95 ${
            variant === 'card' ? 'px-3 py-2' : 'px-4 py-3 border-b border-gray-200'
          }`}
        >
          <div className="flex gap-1">
            <div className={`${variant === 'card' ? 'w-2 h-2' : 'w-3 h-3'} rounded-full bg-red-400`} />
            <div className={`${variant === 'card' ? 'w-2 h-2' : 'w-3 h-3'} rounded-full bg-yellow-400`} />
            <div className={`${variant === 'card' ? 'w-2 h-2' : 'w-3 h-3'} rounded-full bg-green-400`} />
          </div>
          <div className={`flex-1 min-w-0 ${variant === 'card' ? 'mx-2' : 'mx-4'}`}>
            <div
              className={`flex items-center gap-1.5 bg-white/80 rounded border border-gray-200/80 text-gray-500 ${
                variant === 'card' ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1.5 text-sm max-w-md'
              }`}
            >
              {variant === 'panel' ? (
                <Lock className="w-3 h-3 text-green-500 flex-shrink-0" />
              ) : (
                <Monitor className={`${variant === 'card' ? 'w-2.5 h-2.5' : 'w-3 h-3'} flex-shrink-0`} />
              )}
              <span className="truncate">{formatLiveHost(liveUrl)}</span>
            </div>
          </div>
          {variant === 'panel' && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 flex-shrink-0"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Open Full Site
            </a>
          )}
        </div>
      )}

      <div className="relative flex-1 min-h-0 w-full">
        {!canEmbed || iframeError ? (
          fallback
        ) : (
          <>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 z-[1]">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-[#0088cc] animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-400">Loading preview...</p>
                </div>
              </div>
            )}
            <iframe
              src={liveUrl}
              title={`${name} live preview`}
              className={`border-0 ${interactive ? '' : 'pointer-events-none'} ${
                iframeLoaded ? 'opacity-100' : 'opacity-0'
              } transition-opacity duration-500 ${scaled ? '' : 'absolute inset-0 w-full h-full'}`}
              style={
                scaled
                  ? { transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }
                  : undefined
              }
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeError(true)}
              sandbox={
                interactive
                  ? 'allow-scripts allow-same-origin allow-popups allow-forms'
                  : 'allow-scripts allow-same-origin'
              }
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LiveSitePreview;
